import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from '@/redux/hooks'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useSelectedEcommerceIds } from '@/hooks/use-selected-ecommerce-ids'
import { useStoreGroups } from '@/hooks/use-store-groups'
import { findGroupByKey, seesEveryGroup } from '@/domain/store-group'

// useScopePortals lists the portals the caller may put in an analytics scope, with the flags the
// selector shows. It is the only place that knows which portals belong in a return rate.
export function useScopePortals() {
  // With a portal picked in the navbar there is no scope to choose, so the list is not fetched:
  // that view makes exactly the requests it made before this feature existed.
  const navbarPortal = useAppSelector((s) => s.generalData.selectedEcommerce)
  const navbarGroup = useAppSelector((s) => s.generalData.selectedGroup)
  const navbarIds = useSelectedEcommerceIds()
  const groups = useStoreGroups()
  const groupId = findGroupByKey(groups, navbarGroup)?.id
  const user = useAppSelector((s) => s.userApi.getMe.response.user)
  // analytics-api refuses staff without a group, and knows no group for stores without one.
  const canAsk = navbarGroup ? !!groupId : !!user && !seesEveryGroup(user)

  // No range: the tabs do not share one, so a portal is flagged on the server's own 12-month
  // window rather than on a period that may not be the one on screen. The group is only read for
  // REVER staff, who have no group of their own.
  const portalsQuery = useQuery({
    queryKey: ['scopePortals', groupId],
    queryFn: ({ signal }) =>
      AnalyticsApi.getScopePortals({ group: groupId }, signal),
    enabled: !navbarPortal && canAsk,
    staleTime: 1000 * 60 * 15
  })

  // Demo portals answer with fabricated data, so they are kept out of the selector -- except for
  // a group that owns nothing else, where hiding them would leave an empty dashboard.
  const portals = useMemo(() => {
    const all = (portalsQuery.data ?? []).filter(
      (p) => !navbarGroup || navbarIds?.includes(p.id)
    )
    const real = all.filter((p) => !p.isDemo)
    return real.length ? real : all
  }, [portalsQuery.data, navbarGroup, navbarIds])

  return { portals, loadingPortals: portalsQuery.isLoading }
}
